"use client";

import { AlignLeft, ImagePlus, MapPin, Smartphone, Tag, Type } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useId, useState } from "react";

import { uploadDevicePhoto } from "@/lib/api/endpoints/devices";
import { listIndianStates } from "@/lib/api/endpoints/organizations";
import { listAssignableDeviceCategories } from "@/lib/api/endpoints/device-categories";
import { hasModulePermission, useEffectivePermissions } from "@/modules/auth/access";
import { useAuthStore } from "@/modules/auth/store";
import { cn } from "@/lib/utils/cn";
import { ThemedSelect } from "@/components/ui-system";
import { PrivateDeviceImage } from "@/modules/devices/components/private-device-image";

import {
  wizardFieldClass,
  wizardIconBoxClass,
  wizardInvalidInputClass,
  wizardKickerClass,
  wizardLabelClass,
  wizardSectionShellClass,
  wizardTextareaClass,
} from "../form-primitives";
import { LocationPickMap } from "../location-pick-map";
import { useDataloggerWizardStore } from "../../hooks/use-datalogger-form";
import { StepBasicConnectivity } from "./step-basic-connectivity";

const SIM_TYPE_OPTIONS = [
  { value: "phone", label: "Phone" },
  { value: "industrial", label: "Industrial" },
  { value: "other", label: "Other" },
] as const;

export function StepBasic({
  includeConnectivity = true,
  invalidFieldKey = null,
  validationErrors = [],
  onFieldEdit,
  onPhotoUploaded,
}: {
  includeConnectivity?: boolean;
  invalidFieldKey?: string | null;
  validationErrors?: string[];
  onFieldEdit?: () => void;
  /** When set (edit flow), metadata is persisted immediately after a successful upload. */
  onPhotoUploaded?: (path: string) => Promise<void>;
}) {
  const inputId = useId();
  const basic = useDataloggerWizardStore((s) => s.basic);
  const setBasic = useDataloggerWizardStore((s) => s.setBasic);
  const addTag = useDataloggerWizardStore((s) => s.addTag);
  const removeTag = useDataloggerWizardStore((s) => s.removeTag);
  const [tagDraft, setTagDraft] = useState("");
  const [imageError, setImageError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const user = useAuthStore((s) => s.user);
  const { permissionMap } = useEffectivePermissions();
  const canAssignCategory = user?.role === "super_admin" || (user?.accountType === "platform" && hasModulePermission(user, permissionMap, "devices", "edit"));
  const categoriesQuery = useQuery({
    queryKey: ["device-categories", "assignable", basic.deviceCategoryId],
    queryFn: () => listAssignableDeviceCategories(basic.deviceCategoryId || null),
    enabled: canAssignCategory,
    staleTime: 60_000,
  });
  const statesQuery = useQuery({
    queryKey: ["reference", "indian-states"],
    queryFn: listIndianStates,
    staleTime: 24 * 60 * 60 * 1000,
  });
  const stateOptions = (statesQuery.data ?? [])
    .filter((state) => state.is_active)
    .map((state) => ({ value: state.id, label: state.name, description: state.code }));

  const onPickMap = useCallback(
    (lat: number, lng: number) => {
      onFieldEdit?.();
      setBasic({
        latitude: lat.toFixed(6),
        longitude: lng.toFixed(6),
      });
    },
    [onFieldEdit, setBasic],
  );

  async function handleImageFile(file: File) {
    setImageError(null);
    const previewUrl = URL.createObjectURL(file);
    try {
      const uploaded = await uploadDevicePhoto(file);
      onFieldEdit?.();
      setBasic({
        deviceImagePath: uploaded.path,
        deviceImagePreviewUrl: previewUrl,
      });
      if (onPhotoUploaded) {
        try {
          await onPhotoUploaded(uploaded.path);
        } catch (persistError) {
          setImageError(
            persistError instanceof Error ? persistError.message : "Photo uploaded but could not be saved to this device.",
          );
        }
      }
    } catch (e) {
      URL.revokeObjectURL(previewUrl);
      setImageError(e instanceof Error ? e.message : "Could not read image.");
    }
  }

  const nameInvalid = invalidFieldKey === "basic.name";
  const coordinatesInvalid = invalidFieldKey === "basic.coordinates";
  const fieldError = validationErrors[0] ?? null;

  return (
    <div className="space-y-ds-5">
      <div className="grid gap-ds-5 lg:grid-cols-2">
        <section className={wizardSectionShellClass}>
          <div className="relative">
            <div className="mb-ds-5 flex items-center gap-ds-3">
              <div className={wizardIconBoxClass}>
                <Type className="h-4 w-4" aria-hidden />
              </div>
              <div>
                <p className={wizardKickerClass}>Identity</p>
                <p className="text-caption text-muted">How this logger appears in the fleet and dashboards</p>
              </div>
            </div>
            <div className="flex flex-col gap-ds-2">
              <label className={wizardLabelClass}>
                Display name <span className="text-primary">*</span>
              </label>
              <input
                className={cn(wizardFieldClass, nameInvalid && wizardInvalidInputClass)}
                data-validation-key="basic.name"
                aria-invalid={nameInvalid || undefined}
                aria-describedby={nameInvalid ? "basic-name-error" : undefined}
                value={basic.name}
                onChange={(e) => {
                  onFieldEdit?.();
                  setBasic({ name: e.target.value });
                }}
                placeholder="Plant A - main acquisition hut"
                autoComplete="off"
              />
              {nameInvalid && fieldError ? (
                <span id="basic-name-error" className="text-caption text-error">
                  {fieldError}
                </span>
              ) : null}
              {canAssignCategory ? (
                <div className="mt-ds-4 flex flex-col gap-ds-2">
                  <label className={wizardLabelClass}>Device category</label>
                  <ThemedSelect
                    value={basic.deviceCategoryId}
                    options={[{ value: "", label: "No category" }, ...(categoriesQuery.data ?? []).map((category) => ({ value: category.id, label: category.name }))]}
                    ariaLabel="Device category"
                    triggerClassName={wizardFieldClass}
                    onChange={(value) => { onFieldEdit?.(); setBasic({ deviceCategoryId: value }); }}
                  />
                </div>
              ) : null}
            </div>
            <div className="mt-ds-5 border-t border-dashed border-border pt-ds-4">
              <div className="mb-ds-3 flex items-center gap-ds-3">
                <div className={cn(wizardIconBoxClass, "h-7 w-7 [&>svg]:h-3.5 [&>svg]:w-3.5")}>
                  <ImagePlus className="h-3.5 w-3.5" aria-hidden />
                </div>
                <div>
                  <p className={wizardKickerClass}>Data logger photo</p>
                  <p className="text-caption text-muted">Optional - stored privately on the server</p>
                </div>
              </div>
              <div
                role="presentation"
                onDragEnter={(e) => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDragLeave={() => setDragActive(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragActive(false);
                  const f = e.dataTransfer.files?.[0];
                  if (f) void handleImageFile(f);
                }}
                className={cn(
                  "rounded-control border border-dashed border-card-border px-ds-4 py-ds-5 text-center transition-colors",
                  dragActive ? "border-primary/30 bg-primary/8" : "border-border/60 bg-muted-bg/15",
                )}
              >
                <input
                  id={inputId}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="sr-only"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void handleImageFile(f);
                    e.target.value = "";
                  }}
                />
                {basic.deviceImagePreviewUrl ? (
                  <div className="flex flex-col items-center gap-ds-3 sm:flex-row sm:justify-center">
                    <img src={basic.deviceImagePreviewUrl} alt="Device preview" className="h-24 max-w-full rounded-control border border-card-border object-contain shadow-sm" />
                    <div className="flex flex-wrap justify-center gap-ds-2">
                      <label htmlFor={inputId} className="btn btn-secondary btn-sm cursor-pointer !no-underline">Replace photo</label>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setImageError(null); onFieldEdit?.(); setBasic({ deviceImagePath: null, deviceImagePreviewUrl: null }); }}>Remove</button>
                    </div>
                  </div>
                ) : basic.deviceImagePath ? (
                  <div className="flex flex-col items-center gap-ds-3 sm:flex-row sm:justify-center">
                    <PrivateDeviceImage photoPath={basic.deviceImagePath} alt="Device preview" className="h-24 max-w-full rounded-control border border-card-border object-contain shadow-sm" />
                    <div className="flex flex-wrap justify-center gap-ds-2">
                      <label htmlFor={inputId} className="btn btn-secondary btn-sm cursor-pointer !no-underline">Replace photo</label>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setImageError(null); onFieldEdit?.(); setBasic({ deviceImagePath: null, deviceImagePreviewUrl: null }); }}>Remove</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-caption text-muted">Drag and drop an image here, or</p>
                    <label htmlFor={inputId} className="btn btn-primary btn-sm mt-ds-3 cursor-pointer !no-underline">Upload image</label>
                  </>
                )}
              </div>
              {imageError ? <p className="mt-ds-2 text-caption text-error" role="alert">{imageError}</p> : null}
            </div>
          </div>
        </section>

        <section className={wizardSectionShellClass}>
          <div className="mb-ds-5 flex items-center gap-ds-3">
            <div className={wizardIconBoxClass}>
              <Smartphone className="h-4 w-4" aria-hidden />
            </div>
            <div>
              <p className={wizardKickerClass}>SIM details</p>
              <p className="text-caption text-muted">Set the SIM type and related contact / hardware identifiers.</p>
            </div>
          </div>
          <div className="space-y-ds-4">
            <div className="flex flex-col gap-ds-2">
              <label className={wizardLabelClass}>Sim type</label>
              <ThemedSelect
                value={basic.simType}
                onChange={(value) => { onFieldEdit?.(); setBasic({ simType: value as "phone" | "industrial" | "other" }); }}
                options={SIM_TYPE_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
                ariaLabel="SIM type"
                triggerClassName={wizardFieldClass}
              />
            </div>
            {basic.simType !== "industrial" ? (
              <div className="flex flex-col gap-ds-2">
                <label className={wizardLabelClass}>Phone number</label>
                <input className={wizardFieldClass} value={basic.phoneNumber} onChange={(e) => { onFieldEdit?.(); setBasic({ phoneNumber: e.target.value }); }} placeholder="e.g. +91 9876543210" autoComplete="tel" />
              </div>
            ) : null}
            <div className="flex flex-col gap-ds-2">
              <label className={wizardLabelClass}>IMEI number</label>
              <input className={wizardFieldClass} value={basic.imeiNumber} onChange={(e) => { onFieldEdit?.(); setBasic({ imeiNumber: e.target.value }); }} placeholder="15-digit modem / handset IMEI" autoComplete="off" />
            </div>
          </div>
        </section>
      </div>

      {includeConnectivity ? (
        <StepBasicConnectivity
          invalidFieldKey={invalidFieldKey}
          validationErrors={validationErrors}
          onFieldEdit={onFieldEdit}
        />
      ) : null}

      <div className="grid gap-ds-5 lg:grid-cols-2">
        <section className={cn(wizardSectionShellClass, "lg:col-span-2")}>
          <div className="mb-ds-4 flex flex-wrap items-center justify-between gap-ds-3">
            <div className="flex items-center gap-ds-3">
              <div className={cn(wizardIconBoxClass, "h-7 w-7 [&>svg]:h-3.5 [&>svg]:w-3.5")}>
                <MapPin className="h-3.5 w-3.5" aria-hidden />
              </div>
              <div>
                <p className={wizardKickerClass}>Location</p>
                <p className="text-caption text-muted">Site label, coordinates, or pick a point on the map</p>
              </div>
            </div>
          </div>

          <div className="grid gap-ds-5 lg:grid-cols-2">
            <div className="flex flex-col gap-ds-4">
              <div className="flex flex-col gap-ds-2">
                <label className={wizardLabelClass}>Site / asset context</label>
                <input
                  className={wizardFieldClass}
                  value={basic.location}
                  onChange={(e) => {
                    onFieldEdit?.();
                    setBasic({ location: e.target.value });
                  }}
                  placeholder="Building, bay, city, or region"
                  autoComplete="off"
                />
              </div>
              <div className="grid gap-ds-3 sm:grid-cols-2">
                <div className="flex flex-col gap-ds-2">
                  <label className={wizardLabelClass}>City</label>
                  <input
                    className={wizardFieldClass}
                    value={basic.city}
                    onChange={(e) => {
                      onFieldEdit?.();
                      setBasic({ city: e.target.value });
                    }}
                    placeholder="e.g. Ahmedabad"
                    autoComplete="address-level2"
                  />
                </div>
                <div className="flex flex-col gap-ds-2">
                  <label className={wizardLabelClass}>State</label>
                  <ThemedSelect
                    value={basic.stateId}
                    onChange={(value) => {
                      const selected = stateOptions.find((state) => state.value === value);
                      onFieldEdit?.();
                      setBasic({
                        stateId: value,
                        state: selected?.label ?? "",
                      });
                    }}
                    options={[
                      {
                        value: "",
                        label: statesQuery.isLoading ? "Loading states..." : "No state",
                      },
                      ...stateOptions,
                    ]}
                    placeholder="Select state"
                    ariaLabel="State"
                    searchable
                    searchPlaceholder="Search states"
                    triggerClassName={wizardFieldClass}
                    menuClassName="rounded-control"
                  />
                </div>
              </div>
              <div className="grid gap-ds-3 sm:grid-cols-2">
                <div className="flex flex-col gap-ds-2">
                  <label className={wizardLabelClass}>Latitude (deg)</label>
                  <input
                    className={cn(wizardFieldClass, "tabular-nums", coordinatesInvalid && wizardInvalidInputClass)}
                    data-validation-key="basic.coordinates"
                    aria-invalid={coordinatesInvalid || undefined}
                    aria-describedby={coordinatesInvalid ? "basic-coordinates-error" : undefined}
                    value={basic.latitude}
                    onChange={(e) => {
                      onFieldEdit?.();
                      setBasic({ latitude: e.target.value });
                    }}
                    placeholder="e.g. 28.6139"
                    inputMode="decimal"
                    autoComplete="off"
                  />
                </div>
                <div className="flex flex-col gap-ds-2">
                  <label className={wizardLabelClass}>Longitude (deg)</label>
                  <input
                    className={cn(wizardFieldClass, "tabular-nums", coordinatesInvalid && wizardInvalidInputClass)}
                    value={basic.longitude}
                    onChange={(e) => {
                      onFieldEdit?.();
                      setBasic({ longitude: e.target.value });
                    }}
                    placeholder="e.g. 77.2090"
                    inputMode="decimal"
                    autoComplete="off"
                  />
                </div>
              </div>
              {coordinatesInvalid && fieldError ? (
                <p id="basic-coordinates-error" className="text-caption text-error">
                  {fieldError}
                </p>
              ) : null}
              <p className="text-[0.7rem] leading-relaxed text-muted">
                Type coordinates above, or click the map to drop a pin. Leave both blank if you only need a text
                location.
              </p>
            </div>
            <div className="flex flex-col gap-ds-2">
              <p className={wizardLabelClass}>Map</p>
              <LocationPickMap
                latitude={basic.latitude}
                longitude={basic.longitude}
                onPick={onPickMap}
                className="relative overflow-hidden rounded-control border border-card-border bg-surface-secondary"
              />
            </div>
          </div>
        </section>

        <section className={wizardSectionShellClass}>
          <div className="mb-ds-4 flex items-center gap-ds-3">
            <div className={cn(wizardIconBoxClass, "h-7 w-7 [&>svg]:h-3.5 [&>svg]:w-3.5")}>
              <Tag className="h-3.5 w-3.5" aria-hidden />
            </div>
            <p className={wizardKickerClass}>Tags</p>
          </div>

          {basic.tags.length > 0 ? (
            <div className="mb-ds-3 flex flex-wrap gap-ds-2">
              {basic.tags.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => removeTag(t)}
                  className="group/tag inline-flex items-center gap-1 rounded-full border border-border/50 bg-muted-bg/35 px-ds-3 py-0.5 text-[0.7rem] text-foreground/75 transition-all duration-150 hover:border-primary/20 hover:bg-primary/8 hover:text-primary"
                  title={`Remove ${t}`}
                >
                  {t}
                  <span className="text-muted/40 transition-colors group-hover/tag:text-primary">x</span>
                </button>
              ))}
            </div>
          ) : null}

          <div className="flex gap-ds-2">
            <input
              className={cn(wizardFieldClass, "min-w-0 flex-1")}
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              placeholder="site-north - triaxial - line-2"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onFieldEdit?.();
                  addTag(tagDraft);
                  setTagDraft("");
                }
              }}
            />
            <button
              type="button"
              className="btn btn-secondary btn-sm shrink-0"
              onClick={() => {
                onFieldEdit?.();
                addTag(tagDraft);
                setTagDraft("");
              }}
            >
              Add
            </button>
          </div>
        </section>

        <section className={wizardSectionShellClass}>
          <div className="mb-ds-4 flex items-center gap-ds-3">
            <div className={cn(wizardIconBoxClass, "h-7 w-7 [&>svg]:h-3.5 [&>svg]:w-3.5")}>
              <AlignLeft className="h-3.5 w-3.5" aria-hidden />
            </div>
            <p className={wizardKickerClass}>Notes</p>
          </div>
          <div className="flex flex-col gap-ds-2">
            <label className={wizardLabelClass}>Description</label>
            <textarea
              className={cn(wizardTextareaClass, "min-h-[6rem]")}
              value={basic.description}
              onChange={(e) => {
                onFieldEdit?.();
                setBasic({ description: e.target.value });
              }}
              placeholder="Purpose, integrator notes, commissioning checklist reference..."
            />
          </div>
        </section>
      </div>
    </div>
  );
}
